import React, { useState, useMemo } from 'react';
import {
  Button,
  Box,
  TextInput,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
  Text,
} from '@televet/kibble-ui';
import { TwilioConfiguration, useRegisterTwilioSubaccountMutation } from 'shared/types/graphql';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useCallback } from 'react';
interface IRegisterTwilioSubaccountProps {
  canBeTwilioRegistered: boolean;
  twilioConfig?: Maybe<
    {
      __typename?: 'TwilioConfiguration' | undefined;
    } & Pick<TwilioConfiguration, 'messagingServiceSid' | 'subaccountSid'>
  >;
  isClinicSoleProp?: boolean;
}

const StartTwilioRegistration = ({
  canBeTwilioRegistered,
  twilioConfig,
  isClinicSoleProp,
}: IRegisterTwilioSubaccountProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const [createClinicPhoneNumberErrorMessage, setCreateClinicPhoneNumberErrorMessage] = useState('');
  const resetState = useCallback((): void => {
    setCreateClinicPhoneNumberErrorMessage('');
  }, []);
  const [authorizedUserPhone, setAuthorizedUserPhone] = useState('');
  const [authorizedUserEmail, setAuthorizedUserEmail] = useState('');
  const [authorizedUserFirstName, setAuthorizedUserFirstName] = useState('');
  const [authorizedUserLastName, setAuthorizedUserLastName] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [registerTwilioSubaccountMutation, { loading: isLoadingRegisterTwilioSubaccount }] =
    useRegisterTwilioSubaccountMutation();
  const toast = useToast();

  const isAlreadyRegistered = useMemo(() => {
    if (!twilioConfig || !twilioConfig.subaccountSid || !twilioConfig.messagingServiceSid) {
      return false;
    }
  }, [twilioConfig]);

  const onSubmission = useCallback(async () => {
    const missingFields = [
      { field: 'email', value: authorizedUserEmail },
      { field: 'phone', value: authorizedUserPhone },
      { field: 'first name', value: authorizedUserFirstName },
      { field: 'last name', value: authorizedUserLastName },
      { field: 'clinicId', value: currentClinicId },
    ].filter(({ value }) => !value || value.trim() === '' || value.length === 0);
    if (missingFields.length || !currentClinicId) {
      toast({
        title: 'Missing Required Fields!',
        description: 'Make sure all information is added to the form',
        status: 'error',
      });
      return;
    }
    try {
      await registerTwilioSubaccountMutation({
        variables: {
          accountOwnerEmail: authorizedUserEmail,
          accountOwnerPhone: authorizedUserPhone,
          accountOwnerFirstName: authorizedUserFirstName,
          accountOwnerLastName: authorizedUserLastName,
          clinicId: currentClinicId,
        },
      });
      toast({
        title: 'Successfully started Twilio Registration!',
        description:
          'The Brand Registration Process should be underway. Upon approval, the campaign use case will be created. Check the Twilio Subaccount for this clinic for any updates or issues.',
        status: 'success',
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Twilio Registration Failed!',
        description: error.message,
        status: 'error',
      });
    }
  }, [
    authorizedUserEmail,
    authorizedUserPhone,
    authorizedUserFirstName,
    authorizedUserLastName,
    onClose,
    registerTwilioSubaccountMutation,
    toast,
    currentClinicId,
  ]);

  return (
    <Box>
      <Button
        size="sm"
        variant="secondary"
        iconName="plus"
        isLoading={isLoadingRegisterTwilioSubaccount}
        isDisabled={isAlreadyRegistered}
        onClick={(): void => {
          if (!canBeTwilioRegistered) {
            toast({
              title: 'Missing Information',
              description: 'Missing Required Clinic Information - check Address and EIN in the General Info Settings',
              status: 'error',
            });
          } else {
            onOpen();
          }
        }}
      >
        Start Twilio Registration
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={(): void => {
          setCreateClinicPhoneNumberErrorMessage('');
          resetState();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Input Twilio Registration Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isClinicSoleProp ? (
              <Box>
                <Text color="text.danger">
                  <strong>Clinic is a Sole Proprietor!</strong>
                </Text>
                <br></br>
                <Text>
                  Please input the{' '}
                  <Text color="text.danger">
                    <strong>POC clinician&apos;s contact info</strong>
                  </Text>
                  . At some point in the registration process, the phone number you input will get a text message for
                  verification. The owner of the number needs to respond within 24 hours, whereupon the registration
                  process can continue.
                </Text>
              </Box>
            ) : (
              <Text>
                Please input your own contact information into this field, so you can be the clinic&apos;s authorized
                representative. We don&apos;t want to input the clinic&apos;s info here, as it won&apos;t be necessary,
                and Twilio would need to contact us for any technical issues.
              </Text>
            )}

            <VStack justifyContent={'center'} marginTop="20px" spacing="1em">
              <InputGroup>
                <InputLeftAddon>+1</InputLeftAddon>
                <TextInput
                  type="tel"
                  value={authorizedUserPhone}
                  onChange={(event): void => {
                    setAuthorizedUserPhone(event.currentTarget.value);
                  }}
                  placeholder="Authorized User Phone Number"
                />
              </InputGroup>
              <TextInput
                display="block"
                type="email"
                value={authorizedUserEmail}
                onChange={(event): void => {
                  setAuthorizedUserEmail(event.currentTarget.value);
                }}
                placeholder="Authorized User Email"
              />
              <TextInput
                display="block"
                type="text"
                value={authorizedUserFirstName}
                onChange={(event): void => {
                  setAuthorizedUserFirstName(event.currentTarget.value);
                }}
                placeholder="Authorized User First Name"
              />
              <TextInput
                type="text"
                display="block"
                value={authorizedUserLastName}
                onChange={(event): void => {
                  setAuthorizedUserLastName(event.currentTarget.value);
                }}
                placeholder="Authorized User Last Name"
              />
            </VStack>
            {createClinicPhoneNumberErrorMessage && (
              <Box mt={3} color="text.danger">
                {createClinicPhoneNumberErrorMessage}
              </Box>
            )}
          </ModalBody>
          <ModalFooter marginBottom={'8px'}>
            <Button isLoading={isLoadingRegisterTwilioSubaccount} onClick={onSubmission}>
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StartTwilioRegistration;
